import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventStreamItem } from '../types/eventMonitor';

interface EventDetailDrawerProps {
  event: EventStreamItem | null;
  onClose: () => void;
}

/** DrawerRow — label + value grid row */
function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px dashed var(--color-surface-border)',
        fontSize: 'var(--font-size-sm)',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
          fontSize: 'var(--font-size-xs)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: 'var(--color-text-primary)',
          wordBreak: 'break-all',
        }}
      >
        {children}
      </span>
    </div>
  );
}

/** JSON viewer — full payload display */
function JsonViewer({ value, emptyLabel }: { value: string | null; emptyLabel: string }) {
  if (!value) {
    return (
      <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: 'var(--font-size-xs)' }}>
        {emptyLabel}
      </span>
    );
  }

  let formatted = value;
  try {
    formatted = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    // not JSON — display as-is
  }

  return (
    <pre
      style={{
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-xs)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--spacing-3)',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: 'var(--color-text-primary)',
        margin: 0,
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      {formatted}
    </pre>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Seoul',
    });
  } catch {
    return iso;
  }
}

/**
 * EventDetailDrawer — right-side slide drawer on event row click
 * role=dialog · aria-modal=true · focus trap · ESC to close
 */
export function EventDetailDrawer({ event, onClose }: EventDetailDrawerProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [traceCopied, setTraceCopied] = useState(false);
  const [eventIdCopied, setEventIdCopied] = useState(false);

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Focus close button on open
  useEffect(() => {
    if (event) {
      setTimeout(() => closeRef.current?.focus(), 50);
    }
  }, [event]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleCopyTrace = useCallback(async () => {
    if (!event?.traceId) return;
    try {
      await navigator.clipboard.writeText(event.traceId);
      setTraceCopied(true);
      setTimeout(() => setTraceCopied(false), 2000);
    } catch {
      // clipboard API unavailable
    }
  }, [event?.traceId]);

  const handleCopyEventId = useCallback(async () => {
    if (!event?.eventId) return;
    try {
      await navigator.clipboard.writeText(event.eventId);
      setEventIdCopied(true);
      setTimeout(() => setEventIdCopied(false), 2000);
    } catch {
      // clipboard API unavailable
    }
  }, [event?.eventId]);

  if (!event) return null;

  const copyBtnStyle = (copied: boolean): React.CSSProperties => ({
    background: 'transparent',
    border: '1px solid var(--color-surface-border)',
    color: copied ? 'var(--color-success)' : 'var(--color-text-secondary)',
    fontSize: 10,
    padding: '1px var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'color 0.15s',
    marginLeft: 'var(--spacing-2)',
    fontFamily: 'var(--font-family-sans)',
  });

  return (
    <>
      {/* Backdrop overlay */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 200,
        }}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('eventMonitor.drawer.ariaDialog')}
        aria-describedby="em-drawer-desc"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(540px, 90vw)',
          background: 'var(--color-surface-muted)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-6)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {t('eventMonitor.drawer.title')}
            <span
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-brand-primary)',
                background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                padding: '1px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {event.type}
            </span>
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={t('eventMonitor.drawer.ariaClose')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              flexShrink: 0,
              fontFamily: 'var(--font-family-sans)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-surface-muted)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          id="em-drawer-desc"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-4) var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* §1 Basic info */}
          <section
            aria-labelledby="em-drawer-section-meta"
            style={{
              background: 'var(--color-surface-background)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
            }}
          >
            <h3
              id="em-drawer-section-meta"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: '0 0 var(--spacing-3)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
              }}
            >
              {t('eventMonitor.drawer.sectionMeta')}
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                §1
              </span>
            </h3>

            <DrawerRow label={t('eventMonitor.drawer.eventId')}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    wordBreak: 'break-all',
                  }}
                >
                  {event.eventId}
                </span>
                <button
                  type="button"
                  aria-label={t('eventMonitor.drawer.ariaCopyEventId')}
                  onClick={handleCopyEventId}
                  style={copyBtnStyle(eventIdCopied)}
                >
                  {eventIdCopied ? t('eventMonitor.action.copied') : t('eventMonitor.action.copy')}
                </button>
              </span>
            </DrawerRow>

            <DrawerRow label={t('eventMonitor.drawer.type')}>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                }}
              >
                {event.type}
              </span>
            </DrawerRow>

            <DrawerRow label={t('eventMonitor.drawer.source')}>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {event.source}
              </span>
            </DrawerRow>

            <DrawerRow label={t('eventMonitor.drawer.userId')}>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                {event.userId ?? (
                  <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                    {t('eventMonitor.table.system')}
                  </span>
                )}
              </span>
            </DrawerRow>

            <DrawerRow label={t('eventMonitor.drawer.occurredAt')}>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                {formatDateTime(event.occurredAt)} {t('eventMonitor.drawer.occurredAtSuffix')}
              </span>
            </DrawerRow>

            {event.traceId && (
              <DrawerRow label={t('eventMonitor.drawer.traceId')}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {event.traceId}
                  </span>
                  <button
                    type="button"
                    aria-label={t('eventMonitor.drawer.ariaCopyTrace')}
                    onClick={handleCopyTrace}
                    style={copyBtnStyle(traceCopied)}
                  >
                    {traceCopied ? t('eventMonitor.action.copied') : t('eventMonitor.action.copy')}
                  </button>
                </span>
              </DrawerRow>
            )}
          </section>

          {/* §2 Payload JSON */}
          <section
            aria-labelledby="em-drawer-section-payload"
            style={{
              background: 'var(--color-surface-background)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
            }}
          >
            <h3
              id="em-drawer-section-payload"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: '0 0 var(--spacing-3)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
              }}
            >
              {t('eventMonitor.drawer.sectionPayload')}
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                §2
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  fontWeight: 400,
                }}
              >
                {t('eventMonitor.drawer.sectionPayloadNote')}
              </span>
            </h3>
            <JsonViewer
              value={event.payloadSummary}
              emptyLabel={t('eventMonitor.drawer.payloadEmpty')}
            />
          </section>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-elevated)',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('eventMonitor.action.close')}
          </button>
        </div>
      </div>
    </>
  );
}
